import {
    getPromotionTool,
    getPromotionToolSrc,
    PromotionToolEnum,
} from '@/components/images/base64/PromotionTool';
import { ScreenContext } from '@/contexts/ScreenContext';
import {
    PromotionRankfluctuationType,
    usePromotionTool,
} from '@/hooks/popup/promotiontool/usePromotionTool';
import { useIsLobby } from '@/hooks/useIsLobby';
import { RwdTypography } from '@/modules/rwdGame/components/RwdTypography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AudioPlayContext } from '../../../../contexts/AudioPlayContext';
import { RootState } from '../../../../store/store';
import { calcLowHigh, numberFormat } from '../../../../utils/commonFunc';
import { getPromotionToolState } from '../../../main/selector';
import { promotionToolSliceActions } from '../../../main/slice/promotiontool/promotionTool';
import { PromotionPage } from '../../../popup/promotiontool/components/PromotionDialog';
import { popupSliceActions } from '../../slice';
import { PromotionBubble } from './PromotionBubble';
import './index.scss';
import { EventRankingArrow, EventRankingDash } from '../svg';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { CMDPsLastSeenRank } from '@/models/cmd/live';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const billions = 1000000000;
const million = 1000000;
type PromotionIndicatorProps = {
    refHeight?: number | undefined;
};

const PromotionIndicator = (props: PromotionIndicatorProps) => {
    const location = useLocation();
    const { eventEnded, isLeaderboard, fluctuationCheck, EventRank } =
        usePromotionTool();
    const { isLandscape } = useContext(ScreenContext);
    const [screenRatio, setScreenRatio] = useState<number>(1);
    const dispatch = useDispatch();
    const { sendCommand } = useContext(WebSocketContext);
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { Point, RankCount, LastSeenRank } = useSelector((state: RootState) =>
        getPromotionToolState(state)
    );
    const [eventPoint, setEventPoint] = useState<string>(numberFormat(0, 2));
    const cachedRank = useRef(EventRank);
    const defaultHeight = 20;
    const isLobby = useIsLobby();
    const { t } = useTranslation();

    useEffect(() => {
        if (cachedRank.current === 0 || cachedRank.current === LastSeenRank) {
            return;
        } else {
            console.log(
                'promotion:indicator:force update last seen rank since scene changed'
            );
            dispatch(
                promotionToolSliceActions.updateLastSeenRank(cachedRank.current)
            );
        }
    }, [location.pathname]);

    useEffect(() => {
        if (
            !isLeaderboard() ||
            EventRank === 0 ||
            EventRank === cachedRank.current
        )
            return;

        const node = document.querySelector('.button-ranking-arrow');
        if (!node) return;

        const onAnimationEnd = (event: Event) => {
            console.log('promotion:indicator:animationend:', event);
            dispatch(
                promotionToolSliceActions.updateLastSeenRank(cachedRank.current)
            );
        };
        node.addEventListener('animationend', onAnimationEnd);

        cachedRank.current = EventRank;
        const cmd = new CMDPsLastSeenRank();
        cmd.Rank = EventRank;
        sendCommand(cmd);

        return () => {
            node.removeEventListener('animationend', onAnimationEnd);
        };
    }, [EventRank]);

    useEffect(() => {
        if (isLeaderboard()) return;
        const pt = calcLowHigh(Point) / 100;
        if (!isLobby && !isLandscape && pt >= billions) {
            const valueInMillions = pt / million;
            setEventPoint(`${parseFloat(valueInMillions.toFixed(2))}M`);
        } else {
            setEventPoint(numberFormat(pt, 2));
        }
    }, [Point, isLandscape]);

    useEffect(() => {
        if (props.refHeight) {
            setScreenRatio(
                (props.refHeight / defaultHeight) * (isLandscape ? 0.95 : 0.85)
            );
        } else {
            setScreenRatio(1);
        }
    }, [isLandscape, props.refHeight]);

    // useEffect(() => {
    //     setFixedHeight(defaultHeight * screenRatio);
    // }, [screenRatio]);

    const onClickButton = () => {
        if (!isLeaderboard() && eventEnded()) {
            dispatch(
                popupSliceActions.open('promotion_tool.popup_event_ended')
            );
            return;
        }

        dispatch(
            promotionToolSliceActions.updatePage(
                !isLeaderboard() ? PromotionPage.Gacha : PromotionPage.Ranking
            )
        );
        dispatch(promotionToolSliceActions.isOpenDialog(true));
        playButtonAudio();
    };

    const getLeaderboardRanking = () => {
        if (EventRank === 0) return '';

        if (EventRank <= RankCount) {
            if (EventRank === 1)
                return t(`leaderboard.ordinal_1st`).replace(
                    '{{EventRank}}',
                    EventRank.toString()
                );
            if (EventRank > 10 && EventRank < 14)
                return t(`leaderboard.ordinal_th`).replace(
                    '{{EventRank}}',
                    EventRank.toString()
                );
            switch (EventRank % 10) {
                case 1:
                    return t(`leaderboard.ordinal_st`).replace(
                        '{{EventRank}}',
                        EventRank.toString()
                    );
                case 2:
                    return t(`leaderboard.ordinal_nd`).replace(
                        '{{EventRank}}',
                        EventRank.toString()
                    );
                case 3:
                    return t(`leaderboard.ordinal_rd`).replace(
                        '{{EventRank}}',
                        EventRank.toString()
                    );
                default:
                    return t(`leaderboard.ordinal_th`).replace(
                        '{{EventRank}}',
                        EventRank.toString()
                    );
            }
        } else {
            return t(`leaderboard.rank_count`).replace(
                '{{RankCount}}',
                RankCount.toString()
            );
        }
    };

    const getRankFluctuationStyle = () => {
        switch (fluctuationCheck()) {
            case PromotionRankfluctuationType.NoChange:
                return 'button-ranking-arrow hiding';
            case PromotionRankfluctuationType.Rising:
                return 'button-ranking-arrow fade-out';
            case PromotionRankfluctuationType.Falling:
                return 'button-ranking-arrow falling fade-out';
        }
    };

    return (
        <>
            {!eventEnded() && (
                <>
                    {isLobby ? (
                        <Button
                            className="promotion-indicator-button-lobby"
                            onClick={onClickButton}
                        >
                            <Box className="button-detail">
                                <Box
                                    className={`button-icon ${!isLeaderboard() ? '' : 'leaderboard'}`}
                                >
                                    {getPromotionTool(
                                        !isLeaderboard()
                                            ? PromotionToolEnum.event_gem
                                            : PromotionToolEnum.event_trophies
                                    )}
                                </Box>
                                {isLeaderboard() ? (
                                    getLeaderboardRanking() !== '' && (
                                        <Typography className="button-text">
                                            {getLeaderboardRanking()}
                                        </Typography>
                                    )
                                ) : (
                                    <Typography className="button-text">
                                        {eventPoint}
                                    </Typography>
                                )}
                                {isLeaderboard() &&
                                    (EventRank > 0 ? (
                                        <EventRankingArrow
                                            className={getRankFluctuationStyle()}
                                        />
                                    ) : (
                                        <EventRankingDash className="button-ranking-dash" />
                                    ))}
                            </Box>
                            {!isLeaderboard() && (
                                <PromotionBubble
                                    direction="bottom"
                                    spacing={-2}
                                    ratio={screenRatio}
                                    color={'#000000'}
                                />
                            )}
                        </Button>
                    ) : (
                        <Button
                            className="promotion-indicator-button"
                            onClick={onClickButton}
                        >
                            <Box className="button-box">
                                <Box
                                    className={`button-icon ${!isLeaderboard() ? '' : 'leaderboard'}`}
                                >
                                    <img
                                        key={
                                            'PromotionTool_' + !isLeaderboard()
                                                ? PromotionToolEnum.event_gem
                                                : PromotionToolEnum.event_trophies
                                        }
                                        className="PromotionTool"
                                        src={getPromotionToolSrc(
                                            !isLeaderboard()
                                                ? PromotionToolEnum.event_gem
                                                : PromotionToolEnum.event_trophies
                                        )}
                                    />
                                </Box>
                                {isLeaderboard() ? (
                                    getLeaderboardRanking() !== '' && (
                                        <RwdTypography
                                            stepIndex={-1}
                                            landscapeStepIndex={4}
                                            className="button-text"
                                        >
                                            {getLeaderboardRanking()}
                                        </RwdTypography>
                                    )
                                ) : (
                                    <RwdTypography
                                        stepIndex={-1}
                                        landscapeStepIndex={4}
                                        className="button-text"
                                    >
                                        {eventPoint}
                                    </RwdTypography>
                                )}
                                {isLeaderboard() &&
                                    (EventRank > 0 ? (
                                        <EventRankingArrow
                                            className={getRankFluctuationStyle()}
                                        />
                                    ) : (
                                        <EventRankingDash className="button-ranking-dash" />
                                    ))}
                            </Box>
                            {!isLeaderboard() && (
                                <PromotionBubble
                                    direction="left"
                                    spacing={5}
                                    ratio={screenRatio}
                                    color={'#000000'}
                                />
                            )}
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

export default memo(PromotionIndicator);
